"use client";


import { Listbox, Transition } from "@headlessui/react";
import { ArrowDownIcon } from "@ngg/icons";
import type { ComponentProps } from "react";
import { Fragment, useState } from "react";

import { cn } from "@/lib/utils";

type Option = {
  id: any;
  value?: string;
  label?: string;
};

export default function Select({
  children,
  options,
  name,
  required = false,
  disabled = false,
  labelIcon,
  optionIcon,
  preSelectedOption,
  classNames = {},
  placeholder,
}: {
  children?: React.ReactNode;
  placeholder: string;
  name: string;
  options: Option[];
  required?: boolean;
  disabled?: boolean;
  labelIcon?: (opt: Option) => React.ReactNode;
  optionIcon?: (opt: Option) => React.ReactNode;
  preSelectedOption?: Option;
  classNames?: {
    wrapper?: string;
    button?: string;
    option?: string;
    dropdown?: string;
  };
}) {
  const [selectedOption, setSelectedOption] = useState<
    (typeof options)[0] | undefined
  >(preSelectedOption);

  return (
    <>
      <BaseSelect
        name={name}
        value={selectedOption}
        options={options}
        onChange={setSelectedOption}
        required={required}
        disabled={disabled}
        labelIcon={labelIcon}
        optionIcon={optionIcon}
        placeholder={placeholder}
        classNames={classNames}
      />
      {children}
    </>
  );
}

export function BaseSelect({
  name,
  value,
  options,
  onChange,
  required,
  disabled,
  labelIcon,
  optionIcon,
  placeholder,
  classNames = {},
}: {
  options: Option[];
  value?: Option;
  onChange: (opt: Option) => void;
  labelIcon?: (opt: Option) => React.ReactNode;
  optionIcon?: (opt: Option) => React.ReactNode;
  classNames?: {
    wrapper?: string;
    button?: string;
    option?: string;
    dropdown?: string;
  };
} & Omit<ComponentProps<"input">, "value" | "onChange">) {
  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div
        className={cn(
          "relative w-full self-start bg-white text-black",
          classNames.wrapper,
        )}
      >
        <input
          type="text"
          name={name}
          required={required}
          disabled={disabled}
          className="pointer-events-none absolute inset-0 select-none appearance-none bg-transparent text-transparent opacity-0 shadow-none"
          value={value?.id ?? ""}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          aria-readonly="true"
          autoComplete="off"
        />
        <Listbox.Button
          className={cn(
            "group flex w-full items-center border border-black bg-inherit px-4 py-2 text-base text-inherit",
            "peer:dirty:invalid:border-error",
            classNames.button,
          )}
        >
          {() => (
            <>
              {value && labelIcon ? (
                <div className="mr-2 h-4 w-4 flex-shrink-0">
                  {labelIcon(value)}
                </div>
              ) : null}
              {value?.label ? (
                <span>{value?.label}</span>
              ) : (
                <span className="text-grey-300">{placeholder}</span>
              )}
              <ArrowDownIcon
                className={cn([
                  "ml-auto flex-shrink-0 text-lg text-inherit transition-transform group-aria-expanded:-rotate-180",
                  !value?.label && "text-grey-300",
                ])}
                aria-hidden="true"
              />
            </>
          )}
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            className={cn(
              "absolute top-full z-10 mt-0 max-h-40 w-full -translate-y-px overflow-auto border border-black bg-inherit py-1 pl-0 text-inherit",
              classNames.dropdown,
            )}
          >
            {options.map(
              (option) =>
                option.label && (
                  <Listbox.Option
                    key={option.id}
                    value={option}
                    className={cn(
                      "ui-active:bg-gray-100 flex cursor-pointer items-center px-4 py-1 text-base space-x-2",
                      classNames.option,
                    )}
                  >
                    {optionIcon ? (
                      <div className="mr-2 h-4 w-4 flex-shrink-0">
                        {optionIcon(option)}
                      </div>
                    ) : null}
                    {option.label}
                  </Listbox.Option>
                ),
            )}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
